'use client';

import bannerImage from '@/assets/images/social-responsibility/banner.png';
import icon1 from '@/assets/images/social-responsibility/icon-1.svg';
import icon2 from '@/assets/images/social-responsibility/icon-2.svg';
import icon3 from '@/assets/images/social-responsibility/icon-3.svg';
import icon4 from '@/assets/images/social-responsibility/icon-4.svg';
import bannerImageMobile from '@/assets/images/social-responsibility/mobile-banner.png';

import logo1 from '@/assets/images/social-responsibility/logo-1.svg';
import logo2 from '@/assets/images/social-responsibility/logo-2.svg';
import logo3 from '@/assets/images/social-responsibility/logo-3.png';
import { useRef } from 'react';

import couponImage from '@/assets/images/common/coupon.png';
import FormError from '@/components/FormError';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useNewsletter } from '@/hooks/Newsletter/useNewsletter';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from 'antd';
import Image from 'next/image';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const SocialResponsibilityPage = () => {
  const { errors, handleSubmit, isBusy, newsLetterResponse, isModalOpen, setIsModalOpen } = useNewsletter();

  const containerRef = useRef(null);
  let isDragging = false;
  let startX;
  let scrollLeft;

  const handleMouseDown = e => {
    isDragging = true;
    containerRef.current.style.cursor = 'grabbing';
    containerRef.current.style.userSelect = 'none'; // 禁用文本选择
    startX = e.pageX - containerRef.current.offsetLeft;
    scrollLeft = containerRef.current.scrollLeft;
  };

  const handleMouseLeave = () => {
    isDragging = false;
    containerRef.current.style.cursor = 'grab';
    containerRef.current.style.userSelect = ''; // 恢复文本选择
  };

  const handleMouseUp = () => {
    isDragging = false;
    containerRef.current.style.cursor = 'grab';
    containerRef.current.style.userSelect = ''; // 恢复文本选择
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // 滚动速度

    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const FormSchema = z.object({
    email: z.string().trim().email(),
  });

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: '',
    },
  });

  const onButtonClick = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (newsLetterResponse && newsLetterResponse.status) {
      setIsModalOpen(true);
    }
  }, [newsLetterResponse, setIsModalOpen]);

  return (
    <div className='pb-[80px]'>
      <Image
        src={bannerImageMobile}
        className='block h-auto w-full lg_hidden'
        alt=''
        priority
      />
      <Image
        src={bannerImage}
        className='hidden h-auto w-full lg_block'
        alt=''
        priority
      />
      <h1 className='hidden'>Caring for Birds Caring for Nature</h1>

      <div className='block bg-[#EFEFEF] p-[20px] lg_hidden'>
        <p className='font-poppins text-[30px] font-[700] uppercase text-title'>Caring for Birds Caring for Nature</p>
        <p className='mt-[10px] font-sans text-title'>
          At Kingsyard, we believe bird feeding is not just a hobby—it&apos;s a way to connect with and protect the
          natural world .{' '}
        </p>
      </div>

      <div className='mx-auto w-[1100px] max-w-full pt-[40px] lg_pt-[80px]'>
        <h2 className='pl-[15px] font-poppins text-[22px] font-[600] text-title lg_mb-[40px] lg_pl-0 lg_text-center lg_text-[30px]'>
          Our Commitments
        </h2>
        <div className='mx-[15px] mt-[20px] lg_hidden'>
          <div
            id='scroll-container'
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className='flex overflow-x-auto whitespace-nowrap pb-[20px]'
          >
            <div className='mr-[10px] flex min-h-[280px] w-[240px] shrink-0 flex-col items-center justify-center bg-[#F7F7F5] px-[20px] lg_px-[45px]'>
              <div className='flex flex-col items-center justify-center gap-[20px]'>
                <Image
                  src={icon1}
                  className='h-[70px] w-[70px]'
                  alt=''
                />
                <p className='font-poppins text-[18px] font-[600] text-title'>Eco-friendly Materials</p>
              </div>
              <p className='mt-[20px] whitespace-normal text-center font-sans text-sm text-title'>
                We prioritize recyclable and sustainable materials in our packaging and products.
              </p>
            </div>

            <div className='mr-[10px] flex min-h-[280px] w-[240px] shrink-0 flex-col items-center justify-center bg-[#F7F7F5] px-[20px] lg_px-[45px]'>
              <div className='flex flex-col items-center justify-center gap-[20px]'>
                <Image
                  src={icon2}
                  className='h-[70px] w-[70px]'
                  alt=''
                />
                <p className='font-poppins text-[18px] font-[600] text-title'>Wildlife First Designs </p>
              </div>
              <p className='mt-[20px] whitespace-normal text-center font-sans text-sm text-title'>
                All feeders are made with bird safety and well-being in mind.
              </p>
            </div>

            <div className='mr-[10px] flex min-h-[280px] w-[240px] shrink-0 flex-col items-center justify-center bg-[#F7F7F5] px-[20px] lg_px-[45px]'>
              <div className='flex flex-col items-center justify-center gap-[20px]'>
                <Image
                  src={icon3}
                  className='h-[70px] w-[70px]'
                  alt=''
                />
                <p className='font-poppins text-[18px] font-[600] text-title'>Education & Awareness </p>
              </div>
              <p className='mt-[20px] whitespace-normal text-center font-sans text-sm text-title'>
                We share knowledge to help more people understand birds and nature.
              </p>
            </div>

            <div className='mr-[10px] flex min-h-[280px] w-[240px] shrink-0 flex-col items-center justify-center bg-[#F7F7F5] px-[20px] lg_px-[45px]'>
              <div className='flex flex-col items-center justify-center gap-[20px]'>
                <Image
                  src={icon4}
                  className='h-[70px] w-[70px]'
                  alt=''
                />
                <p className='font-poppins text-[18px] font-[600] text-title'>Partnership with Groups </p>
              </div>
              <p className='mt-[20px] whitespace-normal text-center font-sans text-sm text-title'>
                We actively sponsor and collaborate with trusted bird and wildlife organizations to support long-term
                conservation efforts.{' '}
              </p>
            </div>
          </div>
        </div>
        <div className='hidden grid-cols-2 gap-x-[200px] gap-y-[40px] lg_grid'>
          <div className='flex flex-col items-start gap-y-[10px]'>
            <div className='flex items-center justify-start gap-[10px]'>
              <Image
                src={icon1}
                className='lg_h-[90px] lg_w-[90px]'
                alt=''
              />
              <p className='font-poppins text-[24px] font-[600] text-title'>Eco-friendly Materials</p>
            </div>
            <div className='font-sans text-[18px] text-title'>
              We prioritize recyclable and sustainable materials in our packaging and products.
            </div>
          </div>
          <div className='flex flex-col items-start gap-y-[10px]'>
            <div className='flex items-center justify-start gap-[10px]'>
              <Image
                src={icon2}
                className='lg_h-[90px] lg_w-[90px]'
                alt=''
              />
              <p className='font-poppins text-[24px] font-[600] text-title'>Wildlife First Designs </p>
            </div>
            <div className='font-sans text-[18px] text-title'>
              All feeders are made with bird safety and well-being in mind.
            </div>
          </div>
          <div className='flex flex-col items-start gap-y-[10px]'>
            <div className='flex items-center justify-start gap-[10px]'>
              <Image
                src={icon3}
                className='lg_h-[90px] lg_w-[90px]'
                alt=''
              />
              <p className='font-poppins text-[24px] font-[600] text-title'>Education & Awareness </p>
            </div>
            <div className='font-sans text-[18px] text-title'>
              We share knowledge to help more people understand birds and nature.
            </div>
          </div>
          <div className='flex flex-col items-start gap-y-[10px]'>
            <div className='flex items-center justify-start gap-[10px]'>
              <Image
                src={icon4}
                className='lg_h-[90px] lg_w-[90px]'
                alt=''
              />
              <p className='font-poppins text-[24px] font-[600] text-title'>Partnership with Groups </p>
            </div>
            <div className='font-sans text-[18px] text-title'>
              We actively sponsor and collaborate with trusted bird and wildlife organizations to support long-term
              conservation efforts.
            </div>
          </div>
        </div>
      </div>

      <div className='mx-auto w-[1100px] max-w-full px-[15px] pt-[40px] lg_px-0 lg_pt-[80px]'>
        <h2 className='mb-[20px] text-left font-poppins text-[22px] font-[600] text-title lg_mb-[40px] lg_text-center lg_text-[30px]'>
          Organizations We Support
        </h2>

        <div className='flex flex-col items-center justify-between gap-y-[20px] lg_flex-row'>
          <div className='flex w-full flex-col items-center bg-[#EFEFEF] py-[17px] pt-[20px] lg_bg-white lg_py-0'>
            <Image
              src={logo1}
              className='h-auto lg_w-[228px]'
              alt=''
            />
            <p className='mt-[20px] font-poppins text-[18px] font-[600] text-title lg_text-[22px]'>All About Birds</p>
          </div>

          <div className='flex w-full flex-col items-center bg-[#EFEFEF] py-[17px] pt-[20px] lg_bg-white lg_py-0'>
            <Image
              src={logo2}
              className='h-auto lg_w-[128px]'
              alt=''
            />
            <p className='mt-[20px] font-poppins text-[18px] font-[600] text-title lg_text-[22px]'>
              The Hummingbird Society{' '}
            </p>
          </div>

          <div className='flex w-full flex-col items-center bg-[#EFEFEF] py-[17px] pt-[20px] lg_bg-white lg_py-0'>
            <Image
              src={logo3}
              className='h-auto lg_w-[228px]'
              alt=''
            />
            <p className='mt-[20px] font-poppins text-[18px] font-[600] text-title lg_text-[22px]'>Upcoming... </p>
          </div>
        </div>
      </div>

      <div className='mx-auto w-[1100px] max-w-full px-[15px] pt-[80px] lg_px-0'>
        <p className='text-left font-poppins text-[22px] font-[600] leading-[1.4] text-title lg_text-center lg_text-[30px]'>
          Small actions make a big difference.
        </p>
        <p className='text-left font-poppins text-[22px] font-[600] leading-[1.4] text-title lg_text-center lg_text-[30px]'>
          Together, we can make the world a better place-for birds, and for us all.
        </p>

        {
          // newsletter
        }
        <div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className='mt-[20px] flex flex-col justify-center gap-x-[20px] gap-y-[20px] lg_mt-[40px] lg_flex-row'
            >
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem className='max-w-[560px] flex-1'>
                    <FormControl>
                      <Input
                        placeholder='Stay tuned with us!'
                        className='h-[40px] w-full max-w-[560px] rounded-[10px] border-1 border-[#ADADAD] lg_h-[54px]'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormError
                      allowErrorMessages
                      errors={Array.from(errors.values())}
                    />
                  </FormItem>
                )}
              />
              <button
                type='submit'
                disabled={isBusy}
                className='h-[40px] w-full rounded-[10px] bg-primary font-poppins text-sm font-[600] uppercase text-white hover_opacity-90 lg_h-[54px] lg_w-[220px] lg_text-[16px]'
              >
                {isBusy ? 'SUBSCRIBING...' : 'SUBSCRIBE'}
              </button>
            </form>
          </Form>
          <Modal
            open={isModalOpen}
            title={null}
            footer={null}
            width={800}
            onCancel={() => setIsModalOpen(false)}
          >
            <div className='flex flex-col items-center'>
              <h2 className='mt-[45px] font-poppins text-[24px] uppercase leading-[30px] text-primary'>
                thank you for subscribing!
              </h2>
              <p className='mt-2.5 font-sans text-[18px] font-semibold leading-[30px] text-paragraph'>you just got a</p>
              <Image
                src={couponImage}
                className='mt-5 h-auto w-[443px]'
                alt='coupon'
              />
              <p
                className='mt-[50px] cursor-pointer bg-primary px-[56px] font-poppins uppercase leading-[60px] text-white'
                onClick={onButtonClick}
              >
                Shop now
              </p>
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default SocialResponsibilityPage;
